import publicBlogPages from "@/lib/analytics/analytics-blog-pages.json";
import {
  clearClarityCookies,
  getAnalyticsConsent,
  subscribeToAnalyticsConsent,
} from "@/lib/analytics/consent";
import {
  getWebsiteTrackingContext,
  routeTemplateForPath,
} from "@/lib/analytics/website";

type ClarityApi = typeof import("clarity-js")["clarity"];

const PROJECT_ID = "sfa6haegob";
const UPLOAD_URL = "https://p.clarity.ms/collect";
const MAX_PAYLOAD_BYTES = 1024 * 1024;
const MAX_KEEPALIVE_BYTES = 64 * 1024;
const MAX_CONCURRENT_UPLOADS = 2;
const CONTEXT_RETRY_MS = 100;
const MAX_CONTEXT_RETRIES = 20;
const UNSAFE_ANALYTICS_EVENTS = new Set([27, 31, 32, 33, 40, 42]);
const UNSAFE_PLAYBACK_EVENTS = new Set([27, 31, 32, 33, 40, 42]);
const SAFE_DIMENSIONS = new Set([
  0, 1, 2, 4, 9, 15, 16, 17, 22, 23, 24, 25, 26, 28, 29, 33, 34, 35, 36,
  37, 38,
]);
const ELIGIBLE_ROUTE_TEMPLATES = new Set([
  ...Object.keys(publicBlogPages),
  "/",
  "/about",
  "/contact",
  "/pricing",
  "/blog",
  "/blog/:slug",
  "/recruiters",
  "/recruiters/ai-interview-bot",
  "/recruiters/ai-interview-software",
  "/recruiters/ai-interviewer",
  "/recruiters/ai-video-interview-software",
  "/recruiters/ai-mock-interviewer",
  "/recruiters/ai-resume-screening",
  "/recruiters/agentic-resume-search",
  "/recruiters/career-page-builder",
  "/features/ai-interviewer",
  "/features/ai-mock-interview",
  "/features/ai-video-interview",
  "/features/interview-warmup",
  "/auth/login",
  "/auth/register",
]);
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const ABSOLUTE_URL_PATTERN = /https?:\/\/[^\s"'<>\\)\]}]+/gi;
const ATTRIBUTE_URL_PATTERN = /\b(href|src|srcset|action|poster|base)=([^\s"'<>]+)/gi;
const CSS_URL_PATTERN = /url\((['"]?)([^)'"\s]+)\1\)/gi;
const RELATIVE_URL_PATTERN = /(^|[\s"'(=])((?:\.{0,2}\/)[^\s"'<>\\)]*[?#][^\s"'<>\\)]*)/g;
const SENSITIVE_QUERY_VALUE_PATTERN = /([?&](?:access_token|auth|authorization|code|email|key|password|secret|session|token)=[^&#\s"'<>]*)/gi;
const SAFE_ATTRIBUTE_PATTERN = /^(?:class|id|style|type|rel|role|name|method|target|width|height|viewBox|xmlns|d|fill|stroke|transform|aria-hidden|tabindex)=/i;
const REDACTED = "[redacted]";

let clarityApi: ClarityApi | null = null;
let active = false;
let subscribers = 0;
let currentPathname = "";
let generation = 0;
let contextRetries = 0;
let contextTimer: ReturnType<typeof setTimeout> | null = null;
let unsubscribeConsent: (() => void) | null = null;
const uploadControllers = new Set<AbortController>();

const browserSupported = (): boolean => {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    typeof globalThis.fetch !== "function" ||
    typeof globalThis.AbortController !== "function" ||
    typeof globalThis.TextEncoder !== "function"
  ) {
    return false;
  }
  try {
    void document.cookie;
    window.localStorage.getItem("__aiia_replay_support_probe__");
    window.sessionStorage.getItem("__aiia_replay_support_probe__");
    return true;
  } catch {
    return false;
  }
};

const normalizePathname = (pathname: string): string => {
  if (!pathname.startsWith("/")) return "/:unknown";
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
};

export const isReplayEligibleRoute = (pathname: string): boolean =>
  ELIGIBLE_ROUTE_TEMPLATES.has(
    routeTemplateForPath(normalizePathname(pathname))
  );

const actualPathIsCurrentAndEligible = (): boolean =>
  browserSupported() &&
  normalizePathname(window.location.pathname) === currentPathname &&
  isReplayEligibleRoute(window.location.pathname);

const environmentForHostname = (hostname: string): string => {
  const normalized = hostname.toLowerCase();
  if (
    normalized === "staging.aiinterviewagents.com" ||
    normalized.endsWith(".staging.aiinterviewagents.com")
  ) {
    return "staging";
  }
  if (
    normalized === "aiinterviewagents.com" ||
    normalized.endsWith(".aiinterviewagents.com")
  ) {
    return "production";
  }
  return "local";
};

const stripUrlDetails = (value: string): string | null => {
  try {
    const protocolRelative = value.startsWith("//");
    const relative = !/^https?:\/\//i.test(value) && !protocolRelative;
    const parsed = new URL(
      protocolRelative ? `${window.location.protocol}${value}` : value,
      window.location.origin
    );
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    if (relative) return parsed.pathname;
    return protocolRelative
      ? `//${parsed.host}${parsed.pathname}`
      : `${parsed.protocol}//${parsed.host}${parsed.pathname}`;
  } catch {
    return null;
  }
};

const sanitizeString = (input: string): string | null => {
  if (input.length > MAX_PAYLOAD_BYTES) return null;
  let output = input.replace(EMAIL_PATTERN, REDACTED);

  output = output.replace(ABSOLUTE_URL_PATTERN, (url) => {
    const safe = stripUrlDetails(url);
    return safe ?? REDACTED;
  });
  output = output.replace(
    ATTRIBUTE_URL_PATTERN,
    (_match, name: string, url: string) => {
      const safe = stripUrlDetails(url);
      return `${name}=${safe ?? REDACTED}`;
    }
  );
  output = output.replace(
    CSS_URL_PATTERN,
    (_match, quote: string, url: string) => {
      const safe = stripUrlDetails(url);
      return safe ? `url(${quote}${safe}${quote})` : `url(${REDACTED})`;
    }
  );
  output = output.replace(
    RELATIVE_URL_PATTERN,
    (_match, prefix: string, url: string) => {
      const safe = stripUrlDetails(url);
      return `${prefix}${safe ?? REDACTED}`;
    }
  );
  output = output.replace(SENSITIVE_QUERY_VALUE_PATTERN, REDACTED);

  const attribute = /^([^=\s]{1,80})=(.*)$/s.exec(output);
  if (attribute && !SAFE_ATTRIBUTE_PATTERN.test(output)) {
    const name = attribute[1].toLowerCase();
    if (!["href", "src", "srcset", "action", "poster", "base"].includes(name)) {
      return `${attribute[1]}=${REDACTED}`;
    }
  }

  EMAIL_PATTERN.lastIndex = 0;
  const containsEmail = EMAIL_PATTERN.test(output);
  EMAIL_PATTERN.lastIndex = 0;
  return containsEmail ? null : output;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype;

const sanitizeValue = (
  value: unknown,
  budget: { nodes: number },
  depth = 0
): unknown => {
  budget.nodes += 1;
  if (depth > 30 || budget.nodes > 100_000) throw new Error("unsafe payload");
  if (typeof value === "string") {
    const sanitized = sanitizeString(value);
    if (sanitized === null) throw new Error("unsafe string");
    return sanitized;
  }
  if (
    value === null ||
    typeof value === "boolean" ||
    (typeof value === "number" && Number.isFinite(value))
  ) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((entry) => sanitizeValue(entry, budget, depth + 1));
  }
  if (isPlainObject(value)) {
    const output: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      if (!/^[a-z]$/i.test(key)) throw new Error("unexpected payload key");
      output[key] = sanitizeValue(entry, budget, depth + 1);
    }
    return output;
  }
  throw new Error("unexpected payload value");
};

const sanitizeDimensionEvent = (event: unknown[]): unknown[] | null => {
  if ((event.length - 2) % 2 !== 0) return null;
  const output = event.slice(0, 2);
  for (let index = 2; index < event.length; index += 2) {
    const key = event[index];
    if (typeof key !== "number") return null;
    if (SAFE_DIMENSIONS.has(key)) output.push(key, event[index + 1]);
  }
  return output.length > 2 ? output : null;
};

const filterEvents = (value: unknown, blocked: Set<number>): unknown[] => {
  if (!Array.isArray(value)) throw new Error("invalid event collection");
  const output: unknown[] = [];
  for (const event of value) {
    if (
      !Array.isArray(event) ||
      typeof event[1] !== "number" ||
      blocked.has(event[1])
    ) {
      continue;
    }
    if (event[1] === 1) {
      const sanitized = sanitizeDimensionEvent(event);
      if (sanitized) output.push(sanitized);
      continue;
    }
    output.push(event);
  }
  return output;
};

export const sanitizeReplayPayload = (data: string): string | null => {
  if (!browserSupported() || !data || data.length > MAX_PAYLOAD_BYTES) return null;
  try {
    const parsed: unknown = JSON.parse(data);
    if (!isPlainObject(parsed)) return null;
    const keys = Object.keys(parsed);
    if (
      keys.some((key) => !["e", "a", "p"].includes(key)) ||
      !Array.isArray(parsed.e) ||
      parsed.e[4] !== PROJECT_ID
    ) {
      return null;
    }
    const packet: Record<string, unknown> = {
      e: parsed.e,
      a: filterEvents(parsed.a, UNSAFE_ANALYTICS_EVENTS),
    };
    if ("p" in parsed) {
      packet.p = filterEvents(parsed.p, UNSAFE_PLAYBACK_EVENTS);
    }
    const sanitized = sanitizeValue(packet, { nodes: 0 });
    const output = JSON.stringify(sanitized);
    const bytes = new TextEncoder().encode(output).byteLength;
    EMAIL_PATTERN.lastIndex = 0;
    const containsEmail = EMAIL_PATTERN.test(output);
    EMAIL_PATTERN.lastIndex = 0;
    if (bytes > MAX_PAYLOAD_BYTES || containsEmail) return null;
    return output;
  } catch {
    return null;
  }
};

const uploadIsCurrent = (expectedGeneration: number): boolean =>
  active &&
  expectedGeneration === generation &&
  getAnalyticsConsent() === "accepted" &&
  actualPathIsCurrentAndEligible();

const upload = (data: string, expectedGeneration: number): void => {
  if (
    !uploadIsCurrent(expectedGeneration) ||
    uploadControllers.size >= MAX_CONCURRENT_UPLOADS
  ) {
    return;
  }
  const body = sanitizeReplayPayload(data);
  if (!body || !uploadIsCurrent(expectedGeneration)) return;

  let controller: AbortController;
  try {
    controller = new AbortController();
  } catch {
    return;
  }
  uploadControllers.add(controller);
  const bytes = new TextEncoder().encode(body).byteLength;
  void Promise.resolve()
    .then(() => {
      if (!uploadIsCurrent(expectedGeneration)) return;
      return fetch(UPLOAD_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body,
        credentials: "omit",
        keepalive: bytes <= MAX_KEEPALIVE_BYTES,
        signal: controller.signal,
      });
    })
    .catch(() => undefined)
    .finally(() => uploadControllers.delete(controller));
};

const clearContextTimer = (): void => {
  if (contextTimer !== null) clearTimeout(contextTimer);
  contextTimer = null;
  contextRetries = 0;
};

const stopActiveReplay = (clearCookies = false): void => {
  generation += 1;
  active = false;
  clearContextTimer();
  for (const controller of uploadControllers) controller.abort();
  uploadControllers.clear();
  if (clarityApi) {
    try {
      clarityApi.stop();
    } catch {
      /* Optional replay must never affect the app. */
    }
  }
  if (clearCookies) clearClarityCookies();
};

const canStart = (expectedGeneration: number): boolean =>
  subscribers > 0 &&
  expectedGeneration === generation &&
  !active &&
  getAnalyticsConsent() === "accepted" &&
  actualPathIsCurrentAndEligible();

const scheduleContextRetry = (expectedGeneration: number): void => {
  if (!canStart(expectedGeneration) || contextRetries >= MAX_CONTEXT_RETRIES) return;
  contextRetries += 1;
  contextTimer = setTimeout(() => {
    contextTimer = null;
    void startCurrentRoute(expectedGeneration);
  }, CONTEXT_RETRY_MS);
};

const startCurrentRoute = async (expectedGeneration = generation): Promise<void> => {
  if (!canStart(expectedGeneration)) return;
  const context = getWebsiteTrackingContext();
  if (
    !context ||
    context.path !== routeTemplateForPath(currentPathname)
  ) {
    scheduleContextRetry(expectedGeneration);
    return;
  }

  let imported: typeof import("clarity-js");
  try {
    imported = await import("clarity-js");
  } catch {
    return;
  }
  if (!canStart(expectedGeneration)) return;

  clarityApi = imported.clarity;
  try {
    active = true;
    clarityApi.start({
      projectId: PROJECT_ID,
      upload: (data: string) => upload(data, expectedGeneration),
      track: false,
      lean: false,
      content: true,
      mask: [
        "body",
        "video",
        "audio",
        "canvas",
        "iframe",
        "input",
        "textarea",
        "[contenteditable]",
      ],
      unmask: [],
      cookies: [],
      fraud: false,
      diagnostics: false,
      report: null,
      fallback: null,
      modules: [],
    });
    if (!uploadIsCurrent(expectedGeneration)) {
      stopActiveReplay();
      return;
    }
    clarityApi.consentv2({
      analytics_Storage: "granted",
      ad_Storage: "denied",
    });
    clarityApi.identify(
      context.visitorId,
      context.sessionId,
      context.pageViewId
    );
    clarityApi.set(
      "environment",
      environmentForHostname(window.location.hostname)
    );
    clarityApi.set("route", context.path);
  } catch {
    stopActiveReplay();
  }
};

const reconcile = (): void => {
  if (
    getAnalyticsConsent() !== "accepted" ||
    !actualPathIsCurrentAndEligible()
  ) {
    stopActiveReplay(true);
    return;
  }
  if (!active) void startCurrentRoute();
};

export const setReplayRoute = (pathname: string): void => {
  const nextPathname = normalizePathname(pathname);
  if (nextPathname === currentPathname) return;
  const preserveClaritySession =
    getAnalyticsConsent() === "accepted" &&
    (!currentPathname || isReplayEligibleRoute(currentPathname)) &&
    isReplayEligibleRoute(nextPathname);
  stopActiveReplay(!preserveClaritySession);
  currentPathname = nextPathname;
  if (subscribers > 0) reconcile();
};

export const startReplayTracking = (): void => {
  if (!browserSupported()) return;
  subscribers += 1;
  if (subscribers > 1) return;
  if (!currentPathname) currentPathname = normalizePathname(window.location.pathname);
  unsubscribeConsent = subscribeToAnalyticsConsent(reconcile);
  reconcile();
};

export const stopReplayTracking = (): void => {
  subscribers = Math.max(0, subscribers - 1);
  if (subscribers > 0) return;
  unsubscribeConsent?.();
  unsubscribeConsent = null;
  stopActiveReplay(
    getAnalyticsConsent() !== "accepted" || !actualPathIsCurrentAndEligible()
  );
};
